<template>
  <div class="dropzone uploaddocuments uploaddocuments--documents"
    :class="isFailed()
      ? 'dropzone--error'
      : ''
    ">
    <div class="dropzone__content">
      <div v-if="isInitial()" class="text">
        <p>{{ dropzoneStrings.drag_documents }}</p>
      </div>
      <div v-if="isSaving()" class="text">
        <p>{{ dropzoneStrings.is_saving }}</p>
      </div>
      <div v-if="isFailed()" class="text">
        <p>{{ dropzoneStrings.upload_error }}</p>
        <p> {{ uploadError }} </p>
        <p>{{ dropzoneStrings.try_again }}</p>
        <a class="container-link" @click.prevent="reset()"></a>
      </div>
      <LennocModal
        v-if="isReviewing()"
        :domclass="'dropzone--idle dropzone--gradient'"
        :scrollBoundary="scrollBoundary" >
        <div class="dropzone__file-info">
          <div class="dropzone__title">{{ dropzoneStrings.title_type_picker }}</div>
          <div class="text">{{ dropzoneStrings.text_type_picker }}</div>
          <div class="table table--upload">
            <div class="row" v-for="item in fileList" :key="item.FileName">
              <div class="cell cell--link">
                <div class="cell--header">{{ dropzoneStrings.file_name_title }}</div>
                <span v-if="isImage(item)" class="value value--link" @click="viewFile(item)">
                  <LennocIcon  v-if="isImage(item)" image="eye" />
                  {{ item.FileName }}
                </span>
                <span class="value" v-else>{{ item.FileName }}</span>
                <LennocFileView
                  v-if="viewerData"
                  :document="viewerData"
                  :popUpStrings="popUpStrings"
                  :secret="secret"
                  @close="viewerData = null"
                  @download="emits('download', $event)"/>
              </div>
              <div class="cell">
                <span class="cell--header">{{ dropzoneStrings.document_type_title }}</span>
                <LennocMultiSelect
                  :id="item.id"
                  :items="fileTypeCheckItems()"
                  :placeholder="dropzoneStrings.document_type_placeholder"
                  :closeDropdownButtonText="dropzoneStrings.document_type_closedropdownbuttontext"
                  :closeDropdownOnCheckboxClick="closeDropdownOnCheckboxClick"
                  @input="processDocKind($event)"
                />
              </div>
              <div class="cell">
                <div class="cell--header">{{ dropzoneStrings.delete_title }}</div>
                <a class="link link--edit link--action" title="filename" @click="removeFile(item.FileName)">
                  <LennocIcon image="squared-cross" />
                </a>
              </div>
            </div>
          </div>
          <div class="dropzone__controls">
            <div class="hitzone"
              @mouseover="showError = true"
              @mouseleave="showError = false">
              <button
                v-if="isReviewing()"
                data-button="sec"
                class="button button--sec"
                :disabled="!isReady()"
                @click.prevent="startUpload()"
              >
                <span>{{ dropzoneStrings.upload_button }}</span>
                <LennocToolTip v-if="!isReady && showError" :error="true">{{ dropzoneStrings.no_type_error }}</LennocToolTip>
              </button>
            </div>
          </div>
        </div>
      </LennocModal>
    </div>
    <form v-show="isInitial() || isFailed()" ref="uploadForm">
      <input
        type="file"
        multiple
        name="filesToUpload"
        ref="fileControl"
        :disabled="isSaving()"
        @change="filesChange($event)"
        class="input-file"
      />
    </form>
    <LennocIcon v-if="isInitial()" image="upload" />
  </div>
</template>
<script lang="ts" setup>
  import { onMounted, computed, ref, watch, PropType } from 'vue'
  import { FileObject, FileTypeItem, MultiSelectEvent } from '../../types/types'

  const STATUS_INITIAL = 0
  const STATUS_REVIEW = 1
  const STATUS_SAVING = 2
  const STATUS_FAILED = 3

  const props = defineProps({
    claimId: {
      type: String,
      default: ''
    },
    parentDocumentId: {
      type: String,
      default: ''
    },
    questionData: {
      type: Object,
      required: true
    },
    dropzoneStrings: {
      type: Object,
      required: true
    },
    popUpStrings: {
      type: Object,
      required: true
    },
    secret: {
      type: String,
      default: ''
    },
    document: {
      type: Object,
      default: {}
    },
    environment: {
      type: String,
      required: true
    },
    scrollBoundary: {
      type: Number,
      default: 10
    },
    closeDropdownOnCheckboxClick: {
      type: Boolean,
      default: false
    },
    supportedFileTypes: {
      type: Array as PropType<string[]>,
      default: []
    },
    maximumFileSizeInBytes: {
      type: Number,
      default: 9000000
    },
    uploadError: {
      type: String,
      default: ''
    }
  })

  const emits = defineEmits([
    'download',
    'save'
  ])
  
  onMounted(() => {
    reset()
  })

  watch(() => props.uploadError, () => {
    if (props.uploadError) {
      setError(props.uploadError)
    }
  })

  const supportedFileTypesLowerCase = computed(() => {
      return props.supportedFileTypes.map(supportedFileType => supportedFileType.toLowerCase())
  })

  const currentStatus = ref()
  const fileList = ref<Array<FileObject>>([])
  const uploadForm = ref()
  const showError = ref(false)
  const uploadErrorText = ref('')
  const viewerData = ref()

  const addFile = (file : File, index : number) => {
    fileList.value.push({
      id: `droppedFile_${index}`,
      file: file,
      name: file.name,
      FileName: file.name,
      kind: []
    })
  }

  const filesChange = (event : Event) => {
    const unsupported = []
    const filesizeTooLarge = []
    const target = event.target as HTMLInputElement
    const files = target.files as FileList
    Array.from(files).forEach((file, index : number) => {
      if (file.size > props.maximumFileSizeInBytes) {
        filesizeTooLarge.push(file.name)
      }
      if (!isFileSupported(file.name)) {
        unsupported.push(file.name)
      }
      if (!filesizeTooLarge.length && !unsupported.length) {
        addFile(file, index)
      }
    })
    if (filesizeTooLarge.length) {
      setError(props.dropzoneStrings.file_too_large)
    } else if (unsupported.length) {
      setError(props.dropzoneStrings.file_type_not_allowed)
    } else {
      currentStatus.value = STATUS_REVIEW
    }
  }

  const fileTypeCheckItems = () => {
    const uploadInfo = props.questionData.AnswerSchema.Fields[0].FileFields
    return uploadInfo.map((fileType : FileTypeItem ) => {
      return { 
        key: fileType.Name,
        label: getLabelStrings(fileType.Name)
      }
    })
  }

  const getLabelStrings = (key : string) => {
    return props.dropzoneStrings.file_types[key]
  }

  const isFailed = () => {
    return currentStatus.value === STATUS_FAILED
  }

  const isFileSupported = (fileName : string) => {
    if (props.supportedFileTypes === null || props.supportedFileTypes === undefined) {
      return false
    }
    const extension = fileName.substring(fileName.lastIndexOf('.')).toLowerCase()
    return supportedFileTypesLowerCase.value.indexOf(extension) !== -1
  }

  const isImage = (fileObj : FileObject) => {
    return fileObj.file.type.indexOf('image') === 0 ||
      fileObj.file.type.indexOf('pdf') > -1
  }

  const isInitial = () => {
    return currentStatus.value === STATUS_INITIAL
  }
  
  const isReviewing = () => {
    return currentStatus.value === STATUS_REVIEW
  }

  const isSaving = () => {
    return currentStatus.value === STATUS_SAVING
  }
  
  const isReady = () => {
    for (const file of fileList.value) {
      if (Object.keys(file.kind).length) {
        file.tagCheck = true
      } else {
        file.tagCheck = false
      }
    }
    const tagCheck = fileList.value.filter(file => file.tagCheck).length === fileList.value.length
    return tagCheck
  }

  const processDocKind = (event : MultiSelectEvent) => {
    const fileKinds = {} as Record<string, boolean>
    for (const kind of event.value) {
      fileKinds[kind] = true
    }
    const item = fileList.value.find(f => f.id === event.id) as FileObject
    if (item) {
      item.kind = fileKinds
    }
  }

  const removeFile = (fileName : string | undefined) => {
    fileList.value = fileList.value.filter(f => f.FileName !== fileName)
    if (fileList.value.length === 0) {
      reset()
    }
  }

  const reset = () => {
    uploadForm.value.reset()
    currentStatus.value = STATUS_INITIAL
    uploadErrorText.value = ''
    fileList.value = []
    showError.value = false
  }

  const setError = (message : string) => {
    uploadErrorText.value = message
    currentStatus.value = STATUS_FAILED

    // Clear the selected file (list). Selecting a file that is already in the list, will not trigger a change if you try to upload the same file again, because the form thinks that you already selected it.
    uploadForm.value.reset()
    fileList.value = []
  }

  const startUpload = async () => {
    currentStatus.value = STATUS_SAVING
    if (props.environment === 'portal') {
      const formData = new FormData()
      formData.append('claimId', props.claimId)
      formData.append('parentDocumentId', props.parentDocumentId)
      let isActionPerformed = false;
      if( props.document !== null && props.document.DocumentActionPerformedDateTime !== null ) {
        isActionPerformed = true;
      }
      formData.append('isActionPerformed', isActionPerformed.toString())
      for (const[index, f] of fileList.value.entries()) {
        formData.append(`uploadedFile_${index}`, f.file)
        formData.append(`fileKind_${index}`, Object.keys(f.kind).join(','))
      }
      emits('save', formData)
    } else {
      const docId = props.questionData.AnswerSchema.Fields[0].UploadType
      const files = []
      for (const file of fileList.value) {
        files.push( {
          fileObject: file,
          type: docId,
          kind: Object.keys(file.kind).join(', ')
        })
      }
      emits('save', files)
    }
    reset()
  }

  const viewFile = (fileObj : FileObject) => {
    if (isImage(fileObj)) {
      viewerData.value = {
        file: fileObj.file
      }
    }
  }

</script>